import { useState, useCallback, useRef, useEffect } from 'react';

export const useFilterDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState('bottom'); // 'bottom' or 'top'
  const buttonRef = useRef();
  const dropdownRef = useRef();

  // Toggle dropdown
  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Open dropdown
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close dropdown
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Calculate position based on available space
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const dropdownHeight = 400; // Approximate dropdown height

    // If not enough space below and more space above, show above
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setPosition('top');
    } else {
      setPosition('bottom');
    }
  }, []);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      calculatePosition();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close, calculatePosition]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, calculatePosition]);

  return {
    isOpen,
    position,
    buttonRef,
    dropdownRef,
    toggle,
    open,
    close
  };
};